import ActionType, {TActionType} from '../Actions/Constants/ActionType';
import InputType, {TInputType} from '../Actions/Constants/InputType';
import * as Errors from '../Shared/Constants/Errors';
import TActionInstruction from '../Shared/Types/TActionInstruction';
import TFocusManipulator from '../Shared/Types/TFocusManipulator';

import Alignment from './Constants/Alignment';
import FocusableNodeType, {TFocusableNodeType} from './Constants/FocusableNodeType';
import FocusableNode from './FocusableNode';
import IGridFocusedData from './Interfaces/IGridFocusedData';
import ILookupTable from './Interfaces/ILookupTable';
import JustifiedFocusWorker from './JustifiedFocusWorker';
import isJustificationSupported from './Util/isJustificationSupported';

/**
 * A static class containing various utility methods responsible for transferring focus between
 * focusable nodes in response to input methods.
 */
class FocusWorker {
    /**
     * Given a currently focused node, and an input type, returns the next focusable node.
     *
     * @param currentFocusedNode The currently focused node or `null` if no node is in focus.
     * @param root The current root of the three or `null` if no focusable children exist.
     * @param lookupTable A reference to the internal lookup table of focusable nodes.
     * @param focusedAnchorIndex The focused anchor index of the currently focused node
     * @param inputType The input type triggered by the user.
     * @param delegateAction A reference to the `delegateAction` method, to be overridden as needed
     *  in testing.
     * @return A tuple of the the next node to be focused, or `null`, and the index of the anchor to
     *  focus on within that node
     */

    public static getNextFocusedNode(
        currentFocusedNode: FocusableNode | null,
        root: FocusableNode | null,
        lookupTable: ILookupTable,
        focusedAnchorIndex: number,
        inputType: TInputType,
        delegateAction = FocusWorker.delegateAction
    ): [FocusableNode | null, number] {
        let action: TActionInstruction | TFocusManipulator | null = null;
        let childIndices: number[] = [];

        if (!root || inputType === InputType.BLUR) return [null, 0];

        if (!currentFocusedNode) {
            currentFocusedNode = root;

            const {focus} = currentFocusedNode.actions;

            if (typeof focus === 'function') {
                childIndices = focus(null, currentFocusedNode, childIndices);
            } else {
                action = focus;
            }
        }

        if (!action) action = currentFocusedNode!.actions[inputType];

        if (action === ActionType.BLUR) return [null, 0];

        const [nextFocusedNode, nextFocusedAnchorIndex] = delegateAction(
            currentFocusedNode!,
            lookupTable,
            focusedAnchorIndex,
            action,
            inputType,
            childIndices
        );

        return [nextFocusedNode, nextFocusedAnchorIndex];
    }

    /**
     * Given a currently focused node within a "virtual" container, returns the next "non-virtual"
     * index to receive focus, relative to the full dataset.
     *
     * @param currentFocusedNode The currently focused node.
     * @param actionType The action being invoked.
     * @return The new non-virtual focused index, or `-1` to denote no change or a parent boundary.
     */

    public static getNextNonVirtualFocusedIndex(
        currentFocusedNode: FocusableNode,
        actionType: TActionType
    ): number | -1 {
        const focusedParent = currentFocusedNode.parent!;

        if (
            (focusedParent.isVirtualHorizontalList && [ActionType.LEFT, ActionType.RIGHT].indexOf(actionType) < 0) ||
            (focusedParent.isVirtualVerticalList && [ActionType.UP, ActionType.DOWN].indexOf(actionType) < 0)
        ) {
            return -1;
        }

        const currentIndex = currentFocusedNode.index + focusedParent.virtualContainerStartIndex;

        const nextIndex = FocusWorker.getNextListItemIndex(
            focusedParent,
            currentIndex,
            actionType,
            focusedParent.virtualContainerItemsLength - 1
        );

        focusedParent.lastDirectionOfTravel = Math.sign(nextIndex - currentIndex);

        if (nextIndex === currentIndex) return -1;

        return nextIndex;
    }

    /**
     * Given the ID of a registered "virtual" container and "virtual" focused index (relative to a
     * rendered subset of the full dataset), returns the next registered focusable node in the
     * container to receive focus.
     *
     * @param lookupTable A reference to the internal lookup table of focusable nodes.
     * @param parentId The id of a virtual container.
     * @param virtualFocusedIndex The index of the next node to be focused, relative to an
     *   artificial rendered subset of the full dataset.
     * @return The next node to be focused.
     */

    public static getNextVirtualFocusedNode(
        lookupTable: ILookupTable,
        parentId: string,
        virtualFocusedIndex: number
    ): FocusableNode {
        const virtualContainer = lookupTable[parentId];
        const virtualChildNode = virtualContainer.children[virtualFocusedIndex];

        if (virtualChildNode.isContainer) {
            return FocusWorker.focusChild(virtualChildNode, []);
        }

        return virtualChildNode;
    }

    /**
     * Given the current node either in focus or to be focused, and an action, determines the most
     * appropriate behaviour, taking into consideration any possible overrides.
     *
     * @param currentFocusedNode The current node in focus.
     * @param lookupTable A reference to the internal lookup table of focusable nodes.
     * @param action The action to invoke on the next focused node, based on the input type.
     * @param inputType The input type triggered by the user.
     * @param childIndices An array of child indices to be passed through as needed.
     * @param focusChild A reference to the `focusChild` method, to be overridden as needed in
     *  testing.
     * @param focusSibling A reference to the `focusSibling` method, to be overridden as needed in
     *  testing.
     * @return A tuple of the next node to be focused and the index of the anchor to focus on within
     *  that node
     */

    public static delegateAction(
        currentFocusedNode: FocusableNode,
        lookupTable: ILookupTable,
        focusedAnchorIndex: number,
        action: TActionInstruction | TFocusManipulator,
        inputType: TInputType,
        childIndices: number[],
        focusChild = FocusWorker.focusChild,
        focusSibling = FocusWorker.focusSibling
    ): [FocusableNode, number] {
        let nextFocusedNode: FocusableNode = currentFocusedNode;

        if (!Array.isArray(action)) {
            // Simple action

            switch (action) {
                case ActionType.FOCUS_CHILD:
                    return [focusChild(currentFocusedNode, childIndices), focusedAnchorIndex];
                case ActionType.UP:
                case ActionType.DOWN:
                case ActionType.LEFT:
                case ActionType.RIGHT:
                    return focusSibling(
                        currentFocusedNode,
                        lookupTable,
                        focusedAnchorIndex,
                        action,
                        inputType,
                        childIndices
                    );
            }
        } else if (action[0] === ActionType.FOCUS) {
            // Focus override

            const id = action[1];
            const path = action[2] || [];

            nextFocusedNode = lookupTable[id];

            if (!nextFocusedNode) {
                throw new Error(Errors.NO_NODE_FOUND(id));
            }

            if (path.length) {
                nextFocusedNode = focusChild(currentFocusedNode, path);
            }
        }

        return [nextFocusedNode, focusedAnchorIndex];
    }

    /**
     * Given a parent node, and a child index to lookup, this function ensures that the node at that
     * index does not have focus "disabled". If a disabled node is found, its next sibling is
     * returned. If no focusable next sibling is available, the parent is returned.
     *
     * @param parent A parent with one or more children to be looked up or iterated over.
     * @param index The desired index to look up.
     * @return A focusable child node, or the parent if no focusable child nodes are available.
     */

    public static getFocusableChildNodeAtIndex(parent: FocusableNode, index: number): FocusableNode {
        const {children} = parent;

        index = Math.min(children.length - 1, index);

        while (children[index] && children[index].isFocusDisabled) index++;

        if (index === children.length) return parent;

        return children[index];
    }

    /**
     * Given a focused node, iterates through the first child of its first child until a leaf node
     * is found and then returned.
     *
     * If any child indices are provided, the node at the end of the path will be returned instead.
     *
     * @param currentFocusedNode The node currently in focus.
     * @param childIndices An array of child indices pointing to a specific child of the provided
     *  node, to be focused.
     * @return The next node to be focused.
     */

    public static focusChild(currentFocusedNode: FocusableNode, childIndices: number[]): FocusableNode {
        const path = [...childIndices];

        // If the container is a virtual container, focus on its current `virtualFocusedIndex` when
        // it gains focus, else default to `0` (first child)

        const focusChildIndex =
            currentFocusedNode.virtualFocusedIndex > -1 ? currentFocusedNode.virtualFocusedIndex : 0;

        let node = currentFocusedNode;

        // A single loop, to be used in one of two modes, based on whether 1 or more child indices
        // (i.e. a "path"), is provided. Each mode has a different break condition, and means of
        // determining the next child index index to lookup.

        for (;;) {
            if ((childIndices.length > 0 && path.length === 0) || node.children.length === 0) break;

            const nodeAtIndex = FocusWorker.getFocusableChildNodeAtIndex(
                node,
                childIndices.length > 0 ? path.shift()! : focusChildIndex
            );

            if (nodeAtIndex === node) return node;

            node = nodeAtIndex;
        }

        return node;
    }

    public static focusSibling(
        currentFocusedNode: FocusableNode,
        lookupTable: ILookupTable,
        focusedAnchorIndex: number,
        actionType: TActionType,
        inputType: TInputType,
        childIndices: number[],
        delegateAction = FocusWorker.delegateAction
    ): [FocusableNode, number] {
        const {parent, index: currentNodeIndex} = currentFocusedNode;
        const siblings = parent ? parent.children : [];

        if (!parent) {
            // At root, return focus to target child node

            return delegateAction(
                currentFocusedNode,
                lookupTable,
                focusedAnchorIndex,
                ActionType.FOCUS_CHILD,
                inputType,
                childIndices
            );
        }

        let [nextSiblingIndex, nextFocusedAnchorIndex] = FocusWorker.getNextSiblingIndexAndAnchorIndex(
            parent,
            currentNodeIndex,
            focusedAnchorIndex,
            actionType
        );

        if (nextSiblingIndex < 0 || nextSiblingIndex === currentNodeIndex) {
            // At parent boundary, or no node available to take focus, propagate action

            return delegateAction(parent, lookupTable, focusedAnchorIndex, parent.actions[inputType], inputType, [
                currentNodeIndex,
                ...childIndices,
            ]);
        }

        // Sibling available, focus on sibling

        const nextSibling = siblings[nextSiblingIndex];
        const {focus} = nextSibling.actions;

        let nextChildIndices: number[] = [];

        if (typeof focus === 'function') {
            // Focus manipulator available

            nextChildIndices = focus(currentFocusedNode, nextSibling, childIndices);

            // Ensure anchor index is reset when moving to a (non-justified) sibling

            nextFocusedAnchorIndex = 0;
        } else if (
            nextSibling.isContainer &&
            parent.justify &&
            isJustificationSupported(parent.type, nextSibling.type)
        ) {
            const directionOfTravel = FocusWorker.computeDirectionOfTravel(parent.type, actionType);

            // Justified parent container present

            [nextSiblingIndex, nextFocusedAnchorIndex] =
                JustifiedFocusWorker.getClosestChildIndexAndAnchorIndexOfContainer(
                    currentFocusedNode,
                    nextSibling,
                    childIndices,
                    nextFocusedAnchorIndex,
                    directionOfTravel
                );

            nextChildIndices = [nextSiblingIndex];

            if (nextSiblingIndex < 0) {
                // No justified child available to take focus, propagate action

                return delegateAction(parent, lookupTable, focusedAnchorIndex, parent.actions[inputType], inputType, [
                    currentNodeIndex,
                    ...childIndices,
                ]);
            }
        } else {
            // Ensure anchor index is reset when moving to a (non-justified) sibling

            nextFocusedAnchorIndex = 0;
        }

        return delegateAction(
            nextSibling,
            lookupTable,
            nextFocusedAnchorIndex,
            ActionType.FOCUS_CHILD,
            inputType,
            nextChildIndices
        );
    }

    /**
     * Determines the direction of travel or sign based on the combination of list direction and
     * action direction.
     *
     * @param containerType The containing list container component
     * @param actionType A directional action type
     * @return `-1` indicating a decrementing index, `0` indicating no change, or `1` indicating an
     *  incrementing index.
     */

    public static computeDirectionOfTravel(containerType: TFocusableNodeType, actionType: TActionType): -1 | 0 | 1 {
        const isHorizontalContainer =
            [FocusableNodeType.HORIZONTAL_LIST, FocusableNodeType.VIRTUAL_HORIZONTAL_LIST].indexOf(containerType) > -1;

        const isVerticalContainer =
            [FocusableNodeType.VERTICAL_LIST, FocusableNodeType.VIRTUAL_VERTICAL_LIST].indexOf(containerType) > -1;

        const directionOfTravel =
            (isVerticalContainer && actionType === ActionType.DOWN) ||
            (isHorizontalContainer && actionType === ActionType.RIGHT)
                ? 1
                : (isVerticalContainer && actionType === ActionType.UP) ||
                  (isHorizontalContainer && actionType === ActionType.LEFT)
                ? -1
                : 0;

        return directionOfTravel;
    }

    public static getNextListItemIndex(
        parent: FocusableNode,
        currentIndex: number,
        actionType: TActionType,
        maxChildIndex = parent.children.length - 1
    ): number {
        const siblings = parent.children;
        const directionOfTravel = FocusWorker.computeDirectionOfTravel(parent.type, actionType);

        let nextSibling: FocusableNode;
        let isNextSiblingUnfocusable: boolean = false;
        let nextIndex = currentIndex;

        do {
            nextIndex =
                parent.loop && directionOfTravel > 0 && nextIndex === maxChildIndex
                    ? 0
                    : parent.loop && directionOfTravel < 0 && nextIndex === 0
                    ? maxChildIndex
                    : directionOfTravel > 0
                    ? Math.min(maxChildIndex, nextIndex + 1)
                    : directionOfTravel < 0
                    ? Math.max(0, nextIndex - 1)
                    : -1;

            nextSibling = siblings[nextIndex];

            isNextSiblingUnfocusable =
                nextSibling &&
                (nextSibling.isFocusDisabled || (nextSibling.isContainer && nextSibling.children.length === 0));

            // If we are stuck at the start or end of a list due to an focusable node, return the
            // previously focused index.

            if (
                nextSibling &&
                !parent.loop &&
                isNextSiblingUnfocusable &&
                ((directionOfTravel > 0 && nextIndex === maxChildIndex) || (directionOfTravel < 0 && nextIndex === 0))
            ) {
                return currentIndex;
            }

            // // If the next item is a container but it is empty, return focus to source

            // if (nextSibling.isContainer && nextSibling.children.length === 0) {
            //     return currentIndex;
            // }
        } while (nextSibling && isNextSiblingUnfocusable);

        return nextIndex;
    }

    public static getNextGridItemIndexAndAnchorIndex(
        parent: FocusableNode,
        currentIndex: number,
        actionType: TActionType,
        focusedAnchorIndex: number
    ): [number, number] {
        const {columns, children, finalGridRowAlign} = parent;
        const currentRowIndex = Math.floor(currentIndex / columns);

        let rowMin = currentRowIndex * columns;
        let rowMax = Math.min(rowMin + (columns - 1), children.length - 1);

        const currentColumnIndex = currentIndex - rowMin;
        const lastRowIndex = Math.ceil(children.length / columns) - 1;

        // Adjust `rowMin` and `rowMax` inwards as needed to exclude non-focusable nodes

        while (children[rowMin].isFocusDisabled && rowMin <= rowMax) rowMin++;
        while (children[rowMax].isFocusDisabled && rowMax >= rowMin) rowMax--;

        let nextIndex = currentIndex;
        let nextRowIndex = currentRowIndex;
        let nextSibling: FocusableNode | void;
        let nextFocusedAnchorIndex = 0;

        do {
            actionTypeSwitch: switch (actionType) {
                case ActionType.UP:
                case ActionType.DOWN: {
                    let equivalentIndexAtNextRow = -1;

                    nextRowIndex =
                        actionType === ActionType.UP
                            ? Math.max(0, nextRowIndex - 1)
                            : Math.min(lastRowIndex, nextRowIndex + 1);

                    const gridFocusedData: IGridFocusedData = {
                        columns,
                        currentAnchorIndex: focusedAnchorIndex,
                        currentColumnIndex,
                        currentNodeIndex: currentIndex,
                        currentRowIndex,
                        finalGridRowAlign,
                    };

                    [equivalentIndexAtNextRow, nextFocusedAnchorIndex] = FocusWorker.getEquivalentIndexAtNextGridRow(
                        gridFocusedData,
                        nextRowIndex,
                        lastRowIndex,
                        children
                    );

                    if (equivalentIndexAtNextRow === nextIndex) return [currentIndex, focusedAnchorIndex];

                    nextIndex = equivalentIndexAtNextRow;

                    break actionTypeSwitch;
                }

                case ActionType.LEFT:
                    nextIndex = parent.loop && nextIndex === rowMin ? rowMax : Math.max(rowMin, nextIndex - 1);

                    break actionTypeSwitch;
                case ActionType.RIGHT:
                    nextIndex = parent.loop && nextIndex === rowMax ? rowMin : Math.min(rowMax, nextIndex + 1);

                    break actionTypeSwitch;
                default:
                    return [-1, 0];
            }

            nextSibling = children[nextIndex];
        } while (nextSibling && nextSibling.isFocusDisabled);

        return [nextIndex, nextFocusedAnchorIndex];
    }

    private static getNextSiblingIndexAndAnchorIndex(
        parent: FocusableNode,
        focusedNodeIndex: number,
        focusedAnchorIndex: number,
        actionType: TActionType
    ): [number, number] {
        switch (parent.type) {
            case FocusableNodeType.GRID:
                return FocusWorker.getNextGridItemIndexAndAnchorIndex(
                    parent,
                    focusedNodeIndex,
                    actionType,
                    focusedAnchorIndex
                );
            default:
                return [FocusWorker.getNextListItemIndex(parent, focusedNodeIndex, actionType), focusedAnchorIndex];
        }
    }

    private static getEquivalentIndexAtNextGridRow(
        gridFocusedData: IGridFocusedData,
        nextRowIndex: number,
        lastRowIndex: number,
        children: FocusableNode[]
    ): [number, number] {
        const {columns, currentColumnIndex, currentRowIndex, currentNodeIndex, finalGridRowAlign, currentAnchorIndex} =
            gridFocusedData;

        const childCount = children.length;
        const incompleteColumns = childCount % columns;
        const maxIndex = childCount - 1;

        if (
            finalGridRowAlign === Alignment.LEFT ||
            incompleteColumns === 0 ||
            (nextRowIndex !== lastRowIndex && currentRowIndex !== lastRowIndex)
        ) {
            return [Math.min(nextRowIndex * columns + currentColumnIndex, maxIndex), 0];
        }

        switch (finalGridRowAlign) {
            case Alignment.JUSTIFY: {
                const senderNode = children[currentNodeIndex];

                // Start iteration from the first node of the next row

                const firstNodeIndexOnNextRow = nextRowIndex * columns;
                const lastNodeIndexOnNextRow = firstNodeIndexOnNextRow + (columns - 1);

                return JustifiedFocusWorker.findClosestNodeIndexAndAnchorIndex(
                    senderNode,
                    currentAnchorIndex,
                    children,
                    firstNodeIndexOnNextRow,
                    Math.min(children.length - 1, lastNodeIndexOnNextRow),
                    0
                );
            }

            // NB: can only be RIGHT)

            default:
                return [Math.min(nextRowIndex * columns + incompleteColumns + currentColumnIndex, maxIndex), 0];
        }
    }
}

export default FocusWorker;
